import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { default as WaveSurfer } from "wavesurfer.js";
import * as WaveSurferCursorPlugin from "wavesurfer.js/dist/plugin/wavesurfer.cursor.js";
import * as WaveSurferRegionsPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions.js";
import * as WaveSurferTimelinePlugin from "wavesurfer.js/dist/plugin/wavesurfer.timeline.js";
import { addSegmentCut } from "../utils/controls/controlsSegment";
import { wavesurferScroll } from "../utils/controls/timeline";
import {
  editAnnotation,
  hideNote,
  putDescriptionSegment,
  putScriptSegmentSign,
  recommendCharacterCount,
  showNote,
} from "../utils/descriptions/description";
import {
  eachRegions,
  eachRegionsLoop,
  verifiedSegmentsComplete,
  verifiedSegmentsRecordComplete,
  verifiedSegmentsRecordDubbingComplete,
  verifiedSegmentsRecordSignComplete,
  verifiedSegmentsSignComplete,
} from "../utils/regions/regions";
import { putActualTime, secondsToSubrip } from "../utils/time/time";
import { DataUser } from "./DataUser";
import axios from "axios";
import { throttle } from "../utils/performance/debounce";
export const DataDescriptor = createContext();

export const DataProvider = ({ children }) => {
  const { currentProject, user } = useContext(DataUser);
  //REZISE VARIABLES AND FUNCTIONS
  const [resized, setResized] = useState(false);
  const [fullVideo, setFullVideo] = useState(false);
  const [showsrt, setShowsrt] = useState(false);
  //TIME VARIABLES AND FUNCTIONS
  const minLengthRegion = 1;
  const [startHH, setStartHH] = useState(0);
  const [startMM, setStartMM] = useState(0);
  const [startSS, setStartSS] = useState(0);
  const [startMS, setStartMS] = useState(0);

  const [endHH, setEndHH] = useState(0);
  const [endMM, setEndMM] = useState(0);
  const [endSS, setEndSS] = useState(0);
  const [endMS, setEndMS] = useState(0);
  const [duration, setDuration] = useState(0);
  const [actualTime, setActualTime] = useState(0);
  const [timeSubRip, setTimeSubRip] = useState(0);
  const [durationSubRip, setDurationSubRip] = useState(0);
  const [durationAllVideo, setDurationAllVideo] = useState(0);
  const [isActiveAudio, setIsActiveAudio] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [srt, setSrt] = useState("");
  let startTime = {
    hours: startHH,
    minutes: startMM,
    seconds: startSS,
    milliseconds: startMS,
  };
  let endTime = {
    hours: endHH,
    minutes: endMM,
    seconds: endSS,
    milliseconds: endMS,
  };

  //WORDS DESCRIPTION VARIABLES AND FUNCTIONS
  const [wordsCount, setWordsCount] = useState(0);
  const [charactersCount, setCharactersCount] = useState(0);
  const [recommendCharacterMin, setRecommendCharacterMin] = useState(0);
  const [recommendCharacterMax, setRecommendCharacterMax] = useState(0);
  const [description, setDescription] = useState({});
  const [descriptionTest, setDescriptionTest] = useState(null);
  const [comments, setComments] = useState("");
  const [caption, setCaption] = useState("");
  const [dataLength, setDataLength] = useState(0);

  //SEGMENTS VARIABLES AND FUNCTIONS
  const [regions, setRegions] = useState([]);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [currentRegionAudio, setCurrentRegionAudio] = useState(null);
  const [viewAllSegments, setViewAllSegments] = useState(false);
  //WEB SURFER VARIABLES AND FUNCTIONS
  const websurfer = useRef(null);
  const [waveformLoaded, setWaveformLoaded] = useState(false);
  const [stateTimeline, setStateTimeline] = useState(false);
  const [segmentsComplete, setSegmentsComplete] = useState(false);
  
  const regionElementsCache = useRef(new Map());
  const previousRegionId = useRef(null);

  //CONTROL VARIABLES AND FUNCTIONS
  const [zoomLevel, setZoomLevel] = useState(73.4);
  const [loadingZoom, setLoadingZoom] = useState({
    type: "",
    state: false,
  });
  const [loadingWave, setLoadingWave] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(100);
  const [speeds, setSpeeds] = useState([0.5, 0.75, 1, 1.25, 1.5, 2]);
  const [showAudioDescription, setShowAudioDescription] = useState(true);
  const [percent, setPercent] = useState(0);
  const [segmentsProgress, setSegmentsProgress] = useState(0);
  const [isPlayingAction, setIsPlayingAction] = useState(false);
  const handleSpaceRef = useRef();
  const [actionPlay, setActionPlay] = useState(false);
  const [focusDescription, setFocusDescription] = useState(false);

  const audioRef = useRef(null);

  const [clientRevision, setClientRevision] = useState({});

  const getRegionElement = useCallback((regionId) => {
    if (!regionElementsCache.current.has(regionId)) {
      const element = document.querySelector(`region[data-id="${regionId}"]`);
      if (element) {
        regionElementsCache.current.set(regionId, element);
      }
    }
    return regionElementsCache.current.get(regionId);
  }, []);

  const getRegionColor = useCallback((region, currentRegionId, pathname) => {
    if (region.id === currentRegionId) {
      return "rgba(255, 199, 0, 0.3)";
    }
    
    if (region.data?.review?.approved) {
      return "#CCCCCC99";
    }

    if (pathname.includes("/narrator/") || pathname.includes("/narrator-dubbing/")) {
      if (region.data?.audiorecord?.initial && region.data?.audiorecord?.initial !== region.data?.audiorecord?.preview) {
        return "#8EC36A99";
      }
      return "#12B4B899";
    }

    if (pathname.includes("script-adapter")) {
      return region.data?.script ? "#8EC36A99" : "#12B4B899";
    }

    if (pathname.includes("interpreter")) {
      if (region.data?.isMark) {
        return "#000";
      }
      return (region.data?.videorecord || region.data?.blob) ? "#8EC36A99" : "#12B4B899";
    }

    if ((region.data?.description?.initial !== region.data.description?.preview)) {
      return "#8EC36A99";
    }

    return "#12B4B899";
  }, []);

  useEffect(() => {
    createAll();
  }, []);

  useEffect(() => {
    const audioProcessHandler = throttle(() => {
      if (!websurfer.current) return;
      
      let time = websurfer.current.getCurrentTime();
      const duration = websurfer.current.getDuration();
      
      if (duration && !isNaN(duration) && duration > 0) {
        let timeRange = time / duration;
        setActualTime(timeRange);
        setTimeSubRip(secondsToSubrip(time));
      }
    }, 100);

    websurfer.current.on("audioprocess", audioProcessHandler);

    return () => {
      if (websurfer.current) {
        websurfer.current.un("audioprocess", audioProcessHandler);
      }
    };
  }, []);

  const changeViewAllSegments = () => {
    setViewAllSegments(!viewAllSegments);
    const regions = Object.values(websurfer.current.regions.list);
    
    if (!viewAllSegments) {
      regions.forEach((region) => {
        const shouldHide = window.location.href.includes('audio-descriptor')
          ? !(!region.data.review.approved && (region.data.description?.initial === region.data.description.preview || region.data.description?.initial === ''))
          : window.location.href.includes('narrator')
          ? !(!region.data.review.approved && (region.data.audiorecord?.initial === region.data.audiorecord.preview || region.data.audiorecord?.initial === ''))
          : window.location.href.includes('quality-control')
          ? !((region.data.review.comment[0] === region.data.review.comment[1]) && !region.data.review.approved)
          : false;

        if (shouldHide) {
          const regionElement = getRegionElement(region.id);
          if (regionElement) {
            regionElement.style.display = 'none';
          }
        }
      });
    } else {
      regions.forEach((region) => {
        const regionElement = getRegionElement(region.id);
        if (regionElement) {
          regionElement.style.display = 'block';
        }
      });
    }
  }

  const createAll = () => {
    //console.log("CREATE ALL");
    if (websurfer.current === null) {
      const isQC2orClient = (currentProject.rol?.role === "QC2" && currentProject.service !== "dubbing") || currentProject.rol?.role === "CLIENT2";
      
      const objectCreate = {
        container: "#waveform",
        waveColor: "#777082",
        progressColor: "white",
        cursorColor: "white",
        height: 138,
        responsive: true,
        backgroundColor: "#332D36",
        drag: true,
        scrollParent: false,
        waveHeight: 200,
        barWidth: 2,
        barRadius: 2,
        cursorWidth: 1,
        plugins: [
          WaveSurferRegionsPlugin.create({
          }),
          WaveSurferTimelinePlugin.create({
            container: "#waveform-timeline",
            drag: true,
            scrollParent: false,
            //styles
            primaryColor: "#fff",
            secondaryColor: "#fff",
            primaryFontColor: "#fff",
            secondaryFontColor: "#fff",
            primaryLabelInterval: 1,
            secondaryLabelInterval: 1,
            primaryGridInterval: 1,
            secondaryGridInterval: 1,
            primaryGridColor: "#fff",
            secondaryGridColor: "#fff",
            primaryGridOpacity: 0.5,
            secondaryGridOpacity: 0.5,
            primaryLabelAlign: "bottom",
            secondaryLabelAlign: "bottom",
            primaryLabelFormatter: (time) => {
              return `${time}`;
            },
            secondaryLabelFormatter: (time) => {
              return `${time}`;
            },
          }),
          WaveSurferCursorPlugin.create({
            showTime: true,
            opacity: 1,
            color: "#fff",
            customShowTimeStyle: {
              "background-color": "#000",
              color: "#fff",
              padding: "2px",
              "font-size": "10px",
            },

            customShowTimeFunction: (time) => {
              let seconds = Math.floor(time % 60);
              let minutes = Math.floor((time / 60) % 60);
              let hours = Math.floor((time / 3600) % 24);

              if (hours < 10) {
                hours = "0" + hours;
              }
              if (minutes < 10) {
                minutes = "0" + minutes;
              }
              if (seconds < 10) {
                seconds = "0" + seconds;
              }
              return `${hours}:${minutes}:${seconds}`;
            },
          }),
        ],
      };

      // si tiene rol QC2 usar WebAudio, sino MediaElement para video
      if (isQC2orClient) {
        objectCreate.backend = "WebAudio";
        //console.log('🎧 [BACKEND] QC2/CLIENT2 - Usando WebAudio backend para archivo de audio');
      } else {
        objectCreate.backend = "MediaElement";
        //console.log('🎧 [BACKEND] Normal - Usando MediaElement backend para video');
      }


      websurfer.current = WaveSurfer.create(objectCreate);
      createWaveform();
    }
  };

  const setActionsWaveform = () => {
    websurfer.current.on("region-created", (region) => {
      let isEditable = true;
      let addColor = "#12B4B899";

      if (
        (currentProject.rol?.role === "QC2"&& currentProject.service !== "dubbing") ||
        window.location.href.includes("/narrator")
      ) {
        isEditable = false;
      }
      if (region.data?.review?.approved && currentProject.rol?.role === "AD") {
        addColor = "#CCCCCC99";
        isEditable = false;
      }
      if (window.location.href.includes("interpreter")) {

        if (region.data?.isMark) {
          region.update({ color: "#000" });
        }
      }
      region.color = addColor;
      // region.maxLength = 10;// 10 segundos
      region.minLength = (currentProject.service === "dubbing" || currentProject.service === "sign-language-caption") ? 0 : minLengthRegion;
      region.loop = false;
      region.drag = isEditable;
      region.resize = isEditable;
      region.isUpdate = false;
      if (!region?.data?.isMark) {
        setRegions((oldRegions) => [...oldRegions, region]);
        eachRegionsLoop(currentProject.rol?.role, region);
      }
    });
    websurfer.current.on("seek", function (progress) {
      let isRegion = false;
      const duration = websurfer.current.getDuration();
      
      if (duration && !isNaN(duration) && duration > 0) {
        let progressSeconds = progress * duration;
        const keys = Object.keys(websurfer.current.regions.list);
        keys.forEach((key) => {
          const region = websurfer.current.regions.list[key];

          if (
            progressSeconds >= region.start &&
            progressSeconds <= region.end &&
            region.data.id
          ) {
            isRegion = true;
          }
        });

        if (!isRegion) {
          setCurrentRegion(null);
          hideNote(currentRegion, setCaption);
          wavesurferScroll();
        }
      }
    });

    websurfer.current.on("region-click", (region) => {
      editAnnotation(
        user,
        region,
        setCurrentRegion,
        setStartHH,
        setStartMM,
        setStartSS,
        setStartMS,
        setEndHH,
        setEndMM,
        setEndSS,
        setEndMS,
        setDuration,
        setDescription
      );
      if (region.data?.review?.approved && window.location.href.includes("audio-descriptor")) {
        region.update({
          drag: false,
          resize: false,
        });
      }

      if (window.location.href.includes("/narrator") || window.location.href.includes("/client")) {
        region.update({
          drag: false,
          resize: false,
        });
        showNote(region, setCaption);

      } else {
        hideNote(region, setCaption);
      }
    });
    websurfer.current.on("region-update-end", function (region) {
      const regions = Object.values(websurfer.current.regions.list);

      

      const isIntersecting = regions.some((regionItem) => {
        if (regionItem.id !== region.id) {
          return (
            (region.start <= regionItem.start && region.end >= regionItem.start) ||
            (regionItem.start <= region.start && regionItem.end >= region.start)
          );
        }
        return false;
      });

      if (isIntersecting) {
        region.update({
          start: region.data.start,
          end: region.data.end,
        });
        return;

      } else {

        const data = {
          ...region.data,
          start: region.start,
          end: region.end,
        };

        region.update({
          start: region.start,
          end: region.end,
          data,
        });
        region.isUpdate = true;

        const start = secondsToSubrip(region.start);
        const end = secondsToSubrip(region.end);
        setStartHH(start[0]);
        setStartMM(start[1]);
        setStartSS(start[2]);
        setStartMS(start[3]);
        setEndHH(end[0]);
        setEndMM(end[1]);
        setEndSS(end[2]);
        setEndMS(end[3]);
        setDuration(region.end - region.start);
        setCurrentRegion(region);
        if (window.location.href.includes("script-adapter")) {
          putScriptSegmentSign(data, region.data.id)
        } else {
          putDescriptionSegment(data, region.data.id);
        }
      }
    });
    websurfer.current.on("region-in", (region) => {
      if (!region.data?.isMark) {
        const audioRecord = region.data?.audiorecord?.initial ? region.data?.audiorecord?.initial : region.data?.audiorecord?.initial;
        const audio = new Audio(audioRecord);

        audioRef.current = audio;
        setCurrentRegionAudio(region);
        showNote(region, setCaption);
        editAnnotation(
          user,
          region,
          setCurrentRegion,
          setStartHH,
          setStartMM,
          setStartSS,
          setStartMS,
          setEndHH,
          setEndMM,
          setEndSS,
          setEndMS,
          setDuration,
          setDescription
        );
      }
    });

    websurfer.current.on("region-out", (region) => {
      setCurrentRegionAudio(null);
      audioRef.current = null;
    });
  }

  const cutRegion = async (region) => {
    const { start, end } = region;
    const currentTime = websurfer.current.getCurrentTime();

    if (currentTime > start && currentTime < end) {
      if ((currentTime - start < minLengthRegion) || (end - currentTime < minLengthRegion)) return false;
      const data = {
        ...region.data,
        start: region.start,
        end: currentTime,
      }
      region.update({
        start: region.start,
        end: currentTime,
        data: data,
      });

      if (window.location.href.includes("script-adapter")) {
        await putScriptSegmentSign(data, region.data.id)
      } else {
        await putDescriptionSegment(data, region.data.id);
      }
      const newTime = {
        start: currentTime,
        end: end,
      }
      //crear un nuevo region con el nuevo start
      const resp = await addSegmentCut(newTime, currentProject._id);
      if (resp) {
        const { start, end } = resp;
        websurfer.current.addRegion({
          start: start,
          end: end,
          data: {
            id: resp._id,
            description: resp.description,
            comment: resp.comments,
            start: resp.start,
            end: resp.end,
            review: resp.review,
          },
          color: "#12B4B899",
        });
        return true;
      } else {
        return false;
      }
    }
  }

  useEffect(() => {
    if (isActiveAudio === false && currentRegionAudio && !currentRegion) {
      playAudioInWave(currentRegionAudio);
    }
  }, [currentRegionAudio]);

  useEffect(() => {
    return () => {
      if (websurfer.current) {
        const handlers = [
          "audioprocess",
          "region-created",
          "seek",
          "region-click",
          "region-update-end",
          "region-in",
          "region-out",
          "loading",
          "ready",
          "waveform-ready"
        ];
        handlers.forEach(event => {
          try {
            websurfer.current.unAll(event);
          } catch (e) {}
        });
      }
      regionElementsCache.current.clear();
    };
  }, []);

  const playAudioInWave = async () => {
    if (audioRef.current) {
      // audioRef.current.play();
      setIsActiveAudio(true);
    }
  }

  const stopAudioInWave = () => {
    if (audioRef.current) {

      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsActiveAudio(false);
      setCurrentRegionAudio(null);
    }
    setIsReset(false);
  }

  const pauseAudioInWave = () => {
    if (audioRef.current) {

      audioRef.current.pause();
      setIsActiveAudio(false);
    }
  }

  const continuePlayAudioInWave = () => {
    if (audioRef.current) {
      // audioRef.current.play();
      setIsActiveAudio(true);
    }
  }

  const createWaveform = async () => {
   // console.log('🌊 [WAVEFORM] Inicializando waveform y timeline...');
    websurfer.current.disableDragSelection();
    if (regions.length > 0) {
      //console.log(`🧹 Limpiando ${regions.length} regiones anteriores...`);
      websurfer.current.clearRegions();
    }
    setWaveformLoaded(false);
    const file = ((currentProject.rol.role === "QC2" && currentProject.service != "dubbing") ||currentProject.rol?.role === "CLIENT2") ? currentProject.audiofinal : document.querySelector("video");
    const fileType = ((currentProject.rol.role === "QC2" && currentProject.service != "dubbing") ||currentProject.rol?.role === "CLIENT2") ? 'AUDIO FILE' : 'VIDEO ELEMENT';
    
    //console.log(`📁 [WAVEFORM] Tipo de archivo: ${fileType}`);
    //console.log(`🔧 [WAVEFORM] Backend actual: ${websurfer.current.backend?.name || 'WebAudio (por defecto)'}`);
    
    //console.log('⚙️ [WAVEFORM] Configurando acciones del waveform...');
    setActionsWaveform();
    websurfer.current.zoom(zoomLevel);
    
    let eventFired = false;
    let audioLoaded = false;
    
    const loadSegments = async () => {
      if (eventFired) return;
      eventFired = true;
      
      //console.log('✅ [AUDIO] Audio completamente cargado. Iniciando carga de segmentos...');
      setStateTimeline(true);
      setSegmentsProgress(0);
      
      const duration = websurfer.current.getDuration();
      //console.log(`🕐 [DURACIÓN] Obtenida: ${duration}s`);
      
      if (duration && !isNaN(duration) && duration > 0) {
        setDurationSubRip(secondsToSubrip(duration));
        setDurationAllVideo(duration);
       // console.log(`✅ [DURACIÓN] Configurada correctamente: ${duration}s`);
      } else {
        console.warn(`⚠️ [DURACIÓN] Inválida o no disponible: ${duration}. Esperando...`);
        setTimeout(() => {
          const retryDuration = websurfer.current.getDuration();
         // console.log(`🔄 [DURACIÓN] Retry: ${retryDuration}s`);
          if (retryDuration && !isNaN(retryDuration) && retryDuration > 0) {
            setDurationSubRip(secondsToSubrip(retryDuration));
            setDurationAllVideo(retryDuration);
           // console.log(`✅ [DURACIÓN] Configurada en retry: ${retryDuration}s`);
          }
        }, 500);
      }
      
      //console.log('🔄 [SEGMENTOS] Cargando segmentos...');
      await eachRegions(currentProject, user, websurfer, setDataLength, setSegmentsProgress);
      
      //console.log('🎉 [COMPLETO] Todos los segmentos cargados. Mostrando proyecto...');
      setSegmentsProgress(100);
      setWaveformLoaded(true);
    };
    
    websurfer.current.on("loading", (percent) => {
      setPercent(percent);
      if (percent % 20 === 0 || percent === 100) {
        //console.log(`⏳ Cargando audio: ${percent}%`);
      }
      
      if (percent === 100) {
       // console.log('✅ [AUDIO] Audio cargado al 100%!');
        audioLoaded = true;
      }
    });
    
    if ((currentProject.rol.role === "QC2" && currentProject.service != "dubbing") || currentProject.rol.role === "CLIENT2") {
      //console.log('🎧 [MODO] QC2/CLIENT2 - Registrando eventos para WebAudio');
      
      websurfer.current.on("waveform-ready", async () => {
        if (eventFired) return;
        //console.log('✅ [EVENTO] "waveform-ready" disparado!');
        //console.log(`🔍 [WAVEFORM-READY] Backend disponible: ${websurfer.current.backend?.name || 'undefined'}`);
        //console.log(`🔍 [WAVEFORM-READY] Duración: ${websurfer.current.getDuration()}s`);
        await loadSegments();
      });
      
      websurfer.current.on("ready", async () => {
        if (eventFired) return;
        //console.log('✅ [EVENTO] "ready" disparado! (fallback)');
        //console.log(`🔍 [READY] Backend disponible: ${websurfer.current.backend?.name || 'undefined'}`);
        //console.log(`🔍 [READY] Duración: ${websurfer.current.getDuration()}s`);
        const video = document.querySelector("video");
        if (video) video.muted = true;
        await loadSegments();
      });
    } else {
      //console.log('🎧 [MODO] Normal - Registrando evento "ready" para MediaElement');
      
      websurfer.current.on("ready", async () => {
        if (eventFired) return;
        //console.log('✅ [EVENTO] "ready" disparado!');
        //console.log(`🔍 [READY] Backend disponible: ${websurfer.current.backend?.name || 'undefined'}`);
        //console.log(`🔍 [READY] Duración: ${websurfer.current.getDuration()}s`);
        await loadSegments();
      });
      
      websurfer.current.on("waveform-ready", async () => {
        if (eventFired) return;
        //console.log('✅ [EVENTO] "waveform-ready" disparado! (fallback)');
        //console.log(`🔍 [WAVEFORM-READY] Backend disponible: ${websurfer.current.backend?.name || 'undefined'}`);
        //console.log(`🔍 [WAVEFORM-READY] Duración: ${websurfer.current.getDuration()}s`);
        await loadSegments();
      });
    }

    websurfer.current.on("seek", (progress) => {
      setActualTime(progress);
      const duration = websurfer.current.getDuration();
      if (duration && !isNaN(duration) && duration > 0) {
        setTimeSubRip(secondsToSubrip(progress * duration));
        if ((currentProject.rol?.role === "QC2" && currentProject.service !== "dubbing") || currentProject.rol?.role === "CLIENT2") {
          const video = document.querySelector("video");
          if (video) video.currentTime = progress * duration;
        }
      }
    });
    
    //console.log('📥 [WAVEFORM] Iniciando carga del archivo...');
    //console.log(`📝 [BACKEND] Backend configurado: ${websurfer.current.backend?.name || 'undefined'}`);
    try {
      await websurfer.current.load(file);
      //console.log('✅ [WAVEFORM] load() completado');
      //console.log(`🔍 [POST-LOAD] Backend: ${websurfer.current.backend?.name || 'undefined'}`);
      //console.log(`🔍 [POST-LOAD] isReady: ${websurfer.current.isReady}`);
      //console.log(`🔍 [POST-LOAD] Duración: ${websurfer.current.getDuration()}s`);
      
    } catch (e) {
      console.error('❌ [ERROR] Error cargando archivo:', e);
    }

  };

  useEffect(() => {
    if (regions.length === 0 || window.location.href.includes("quality-control")) {
      return;
    }

    const pathname = window.location.href;
    
    if (previousRegionId.current && previousRegionId.current !== currentRegion?.id) {
      const prevRegion = regions.find(r => r.id === previousRegionId.current);
      if (prevRegion) {
        const prevIcon = getRegionElement(prevRegion.id)?.querySelector('.loop');
        if (prevIcon) {
          prevIcon.style.background = "rgba(0, 0, 0, 0)";
        }
        
        const color = getRegionColor(prevRegion, null, pathname);
        prevRegion.update({ drag: false, color });
      }
    }

    if (currentRegion) {
      //console.log(`🎯 [REGIÓN] Seleccionada: Segmento ${regions.findIndex(r => r.id === currentRegion.id) + 1}/${regions.length} | ID: ${currentRegion.id}`);
      
      const currentIcon = getRegionElement(currentRegion.id)?.querySelector('.loop');
      if (currentIcon) {
        currentIcon.style.background = "rgba(255, 199, 0)";
      }

      const color = getRegionColor(currentRegion, currentRegion.id, pathname);
      currentRegion.update({ drag: false, color });

      setDescription(currentRegion.data.description || "");
      setComments(currentRegion.data.comment || "");
      
      previousRegionId.current = currentRegion.id;
    } else {
      const icons = document.querySelectorAll(".loop");
      icons.forEach((icon) => icon.style.background = "rgba(0, 0, 0, 0)");
      
      previousRegionId.current = null;
    }
  }, [currentRegion?.id, regions.length]);

  useEffect(() => {
    let isComplete = false;
    if (regions.length > 0 && window.location.href.includes('descriptor')) {
      isComplete = verifiedSegmentsComplete(websurfer);
    } else if (window.location.href.includes('/narrator/')) {
      isComplete = verifiedSegmentsRecordComplete(websurfer);
    } else if (window.location.href.includes('/narrator-dubbing/')) {
      isComplete = verifiedSegmentsRecordDubbingComplete(websurfer);
    } else if (window.location.href.includes('script-adapter')) {
      isComplete = verifiedSegmentsSignComplete(websurfer);
    } else if (window.location.href.includes('interpreter')) {
      isComplete = verifiedSegmentsRecordSignComplete(websurfer);
    }
    setSegmentsComplete(isComplete);

    if (currentRegion && !window.location.href.includes("narrator-dubbing")) {
      recommendCharacterCount(
        currentRegion.data?.description?.initial,
        duration,
        setCharactersCount,
        setRecommendCharacterMin,
        setRecommendCharacterMax
      );
    }
  }, [regions, description, duration, currentRegion]);

  useEffect(() => {
    //console.log("slowVolume",volume);
     websurfer.current.setVolume(volume / 100); }, [volume]);

  const playPauseAfter3Seconds = useCallback(
    async (endTime) => {
      if (websurfer.current?.isPlaying()) {

        websurfer.current.pause();
        setIsPlaying(false);

      } else {
        const startTime = websurfer.current.getCurrentTime();
        await websurfer.current.play(startTime, endTime);
        setIsPlaying(true);

      }
      return websurfer.current.getDuration();
    },
    [isPlayingAction]
  );

  const playPause = useCallback(async () => {
    if (isPlayingAction) return; // Evita que la función se llame varias veces
    setIsPlayingAction(true);

    if (await websurfer.current?.isPlaying()) {
      websurfer.current.pause();
      if ((currentProject.rol.role === "QC2" && currentProject.service != "dubbing") ||currentProject.rol?.role === "CLIENT2") {
        const video = document.querySelector("video");
        video.pause();
      }

      if (!currentRegion) {
        pauseAudioInWave();
      }

      setIsPlaying(false);

    } else {
      await websurfer.current.play();
      if ((currentProject.rol.role === "QC2"&& currentProject.service != "dubbing") ||currentProject.rol?.role === "CLIENT2") {
        const video = document.querySelector("video");
        video.play();
      }
      if (!currentRegion) {
        continuePlayAudioInWave();
      }
      setIsPlaying(true);
    }

    setIsPlayingAction(false);
  }, [websurfer, setIsPlaying, isPlayingAction, currentRegion, currentRegionAudio]);

  useEffect(() => {

    handleSpaceRef.current = (e) => {
      if (e.key === " ") {
        // Verifica si el elemento activo es un input o textarea
        if (
          document.activeElement.tagName === "INPUT" ||
          document.activeElement.tagName === "TEXTAREA"
        ) {
          return;
        }
        e.preventDefault();
        //toggle estado de play/pause
        setIsPlayingAction(!isPlayingAction);

        playPause();
      }
    };

    document.addEventListener("keydown", handleSpaceRef.current);

    return () => {
      document.removeEventListener("keydown", handleSpaceRef.current);
    };
  }, [playPause]);


  // useEffect(() => {
  //   (async () => {
  //     const response = await fetch(`${process.env.REACT_APP_URL}/api/projects/${currentProject._id}`, { method: "GET" });
  //     const result = await response.json();
  //     setClientRevision({
  //       AD: result?.project?.client[0],
  //       AN: result?.project?.client[1],
  //     });
  //     console.log({
  //       AD: result?.project?.client[0],
  //       AN: result?.project?.client[1],
  //     });
  //   })();
  // }, [currentRegion]);
  const handleSetLoadingZoom = (type, state) => {
    setLoadingZoom({
      type: type,
      state: state,
    })
  }
  const generateFrameDescription = async (segmentId, videoPath, timeSec, segmentDuration,language,tone) => {
    return axios.post(`${process.env.REACT_APP_URL}/api/projects/generate-frame-description2/${segmentId}`, {
        videoPath,
        timeSec,
        segmentDuration,
        language,
        tone
      }
    )
    .then(res => {
      //console.log("Response from generateFrameDescription:", res.data); // Muestra la respuesta en la consola
      return res.data;
    })
    .catch(error => {
      //console.error("Error generating frame description:", error);
      throw error;
    });
  };
  const generateFrameDescriptionwithbible = async (segmentId, videoPath, timeSec, segmentDuration,bibleid) => {
    return axios.post(`${process.env.REACT_APP_URL}/api/projects/generate-frame-description-with-bible/${segmentId}`, {
        videoPath,
        timeSec,
        segmentDuration,
        bibleid
      }
    )
    .then(res => {
      //console.log("Response from generateFrameDescription:", res.data); // Muestra la respuesta en la consola
      return res.data;
    })
    .catch(error => {
      //console.error("Error generating frame description:", error);
      throw error;
    });
  };
  const fetchSegmentById = async (segmentId) => {
    try {
        //console.log(`📡 Obteniendo segmento con ID: ${segmentId}...`);

        const response = await fetch(`${process.env.REACT_APP_URL}/api/segments/segment/${segmentId}`);
        if (!response.ok) {
            throw new Error(`Error en la solicitud: ${response.statusText}`);
        }

        const segment = await response.json();

        if (!segment) {
            //console.warn("⚠️ No se encontró el segmento.");
            return null;
        }

        //console.log("✅ Segmento obtenido:", segment);

        return segment;
    } catch (error) {
        //console.error("❌ Error al obtener el segmento:", error);
        return null;
    }

};

  return (
    <DataDescriptor.Provider
      value={{
        startTime,
        endTime,
        currentRegion,
        setCurrentRegion,
        setStartHH,
        setStartMM,
        setStartSS,
        setStartMS,
        setEndHH,
        setEndMM,
        setEndSS,
        setEndMS,
        setDuration,
        duration,
        waveformLoaded,
        zoomLevel,
        playPause,
        setComments,
        setDescription,
        description,
        comments,
        isPlaying,
        setTimeSubRip,
        setVolume,
        volume,
        wordsCount,
        actualTime,
        setActualTime,
        timeSubRip,
        durationSubRip,
        putActualTime,
        charactersCount,
        recommendCharacterMin,
        recommendCharacterMax,
        regions,
        percent,
        segmentsProgress,
        setSegmentsProgress,
        websurfer,
        setRecommendCharacterMin,
        setRecommendCharacterMax,
        resized,
        setResized,
        fullVideo,
        setFullVideo,
        segmentsComplete,
        playPauseAfter3Seconds,
        durationAllVideo,
        setZoomLevel,
        startHH,
        startMM,
        startSS,
        startMS,
        endHH,
        endMM,
        endSS,
        endMS,
        currentProject,
        setRegions,
        user,
        setWordsCount,
        setCharactersCount,
        setIsPlaying,
        caption,
        isActiveAudio,
        setIsActiveAudio,
        setActionPlay,
        stopAudioInWave,
        isReset,
        setIsReset,
        setCurrentRegionAudio,
        currentRegionAudio,
        showAudioDescription,
        setShowAudioDescription,
        speeds,
        pauseAudioInWave,
        setCaption,
        clientRevision,
        setClientRevision,
        focusDescription,
        setFocusDescription,
        cutRegion,
        descriptionTest,
        setDescriptionTest,
        loadingZoom,
        handleSetLoadingZoom,
        loadingWave, setLoadingWave,
        changeViewAllSegments, viewAllSegments, dataLength,minLengthRegion,
        generateFrameDescription,
        fetchSegmentById,
        showsrt,
        setShowsrt,
        srt,
        setSrt,
        generateFrameDescriptionwithbible
      }}
    >
      {children}
    </DataDescriptor.Provider>
  );
};
